import React, { useContext } from "react";
import PropTypes from "prop-types";
import { Config, formatPrice, I18n, I18nContext } from "@deporvillage/utils-library-component";
import Picture from "@deporvillage/picture-component";
import FooterReviews from './FooterReviews';

import styles from './FooterMobile.module.scss';

const FooterContent = ({ showBestPrice, showFinance, rating }) => {

    const { translate } = useContext(I18nContext);
    const { country, store, lang, freeDeliveryThreshold, trustpilot } = useContext(Config);
    const isFreeDelivery = freeDeliveryThreshold && store.code === country.code;

    return (
        <div className={styles['footer-mobile-content']}>

            {
                trustpilot?.enabled?.widget && (
                    <FooterReviews rating={rating}/>
                )
            }

            <section className={styles['footer-mobile-links']}>
                <ul>
                    <li>
                        <a href={translate('footer.services.shipping_link')} rel='nofollow'>
                            <i className={`${styles['icon-link']} icon icon-transport`} />
                            <span dangerouslySetInnerHTML={{
                                __html: isFreeDelivery ? translate('footer.services.shipping_free', {
                                    shipping_cost: formatPrice(
                                        freeDeliveryThreshold,
                                        country.currency_code,
                                        lang,
                                        {
                                            decimalDigits: 0,
                                            decimalPadding: 0,
                                        }
                                    ),
                                }) : translate('footer.services.shipping_express')
                            }} />
                        </a>
                    </li>
                    <li>
                        <a href={translate('footer.services.delivery_link')} rel='nofollow'>
                            <i className={`${styles['icon-link']} icon icon-schedule`} />
                            <span dangerouslySetInnerHTML={{
                                __html: translate(
                                    isFreeDelivery
                                        ? 'footer.services.express_delivery'
                                        : 'footer.services.delivery'
                                ),
                            }} />
                        </a>
                    </li>
                    <li>
                        <a href={translate('footer.services.return_link')} rel='nofollow'>
                            <i className={`${styles['icon-link']} icon icon-refresh`} />
                            <span dangerouslySetInnerHTML={{
                                __html: translate('footer.services.return'),
                            }} />
                        </a>
                    </li>

                    {!showFinance ? null : (
                        <li>
                            <a href={translate('footer.services.finance_link')} rel='nofollow'>
                                <i className={`${styles['icon-link']} icon icon-credit-card`} />
                                <span dangerouslySetInnerHTML={{
                                    __html: translate('footer.services.finance'),
                                }} />
                            </a>
                        </li>
                    )}

                    {!showBestPrice ? null : (
                        <li>
                            <a href={translate('footer.services.price_link')} rel='nofollow'>
                                <i className={`${styles['icon-link']} icon icon-tag`} />
                                <span dangerouslySetInnerHTML={{
                                    __html: translate('footer.services.price'),
                                }} />
                            </a>
                        </li>
                    )}

                    <li>
                        <a href={translate('footer.services.help_link')} rel='nofollow'>
                            <i className={`${styles['icon-link']} icon icon-info-outline`} />
                            <span dangerouslySetInnerHTML={{
                                __html: translate('footer.services.help'),
                            }} />
                        </a>
                    </li>
                </ul>
            </section>
        </div>
    );
};

FooterContent.propTypes = {
    showFinance: PropTypes.bool,
    showBestPrice: PropTypes.bool,
    rating: PropTypes.object,
};

const FooterSprite = ({ icons }) => {
    const { paymentMethods, shippingMethods, security, mobileApp } = icons;
    const { lang } = useContext(Config);
    return (
        <div className={styles['footer-mobile-icon']}>
            {mobileApp.length ? (
                <div className={styles['footer-mobile-icon-wrapper']}>
                    <p className={styles['footer-mobile-icon-title']}>
                        <strong>
                            <I18n>footer.sprite.app_deporvillage</I18n>
                        </strong>
                    </p>

                    {mobileApp.map(m => (
                        <a href={m.url} className={`${styles[`sprite-footer-${lang}`]} ${m.css}`} key={m.type} rel="nofollow noopener noreferrer" target="_blank" title={m.type}>
                            <Picture lazy src={`/static/layout/icons/${m.img}`} width={m.width} name={m.method} />
                        </a>
                    ))}
                </div>
            ) : null}

            {paymentMethods.length && (
                <div className={`${styles['footer-mobile-icon-wrapper']} ${styles['grayscale']}`}>
                    {paymentMethods.map(m =>
                        m.url ? (
                            <a className={styles[`sprite-footer-${lang}`]} href={m.url} rel="nofollow noopener noreferrer" title={m.method} target="_blank" key={m.method}>
                                <Picture lazy src={`/static/layout/icons/${m.img}`} width={m.width} name={m.method} />
                            </a>
                        ) : (
                            <span className={styles[`sprite-footer-${lang}`]} key={m.method}>
                                <Picture lazy src={`/static/layout/icons/${m.img}`} width={m.width} name={m.method} />
                            </span>
                        )
                    )}
                </div>
            )}

            {shippingMethods.length && (
                <div className={`${styles['footer-mobile-icon-wrapper']} ${styles['grayscale']}`}>
                    {shippingMethods.map(m => (
                        <span className={styles[`sprite-footer-${lang}`]} key={m.method}>
                            <Picture lazy src={`/static/layout/icons/${m.img}`} width={m.width} name={m.method} />
                        </span>
                    ))}
                </div>
            )}

            {security.length && (
                <div className={`${styles['footer-mobile-icon-wrapper']} ${styles['grayscale']}`}>
                    {security.map(t => (
                        <span className={styles[`sprite-footer-${lang}`]} key={t.type}>
                            <Picture lazy src={`/static/layout/icons/${t.img}`} width={t.width} name={t.method} />
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

FooterSprite.propTypes = {
    icons: PropTypes.object
};

const FooterXXSS = () => {
    const { translate } = useContext(I18nContext);
    return (
        <div className={styles['footer-mobile-xxss']}>
            <a rel="nofollow noopener noreferrer" href={translate("footer.rrss.facebook")} target="_blank" className={styles['xxss']}>
                <span className="icon icon-facebook-f" />
            </a>
            <a rel="nofollow noopener noreferrer" href={translate("footer.rrss.instagram")} target="_blank" className={styles['xxss']}>
                <span className="icon icon-instagram icon-expand" />
            </a>
            <a rel="nofollow noopener noreferrer" href={translate("footer.rrss.strava")} target="_blank" className={styles['xxss']}>
                <span className="icon icon-strava icon-expand" />
            </a>
            <a rel="nofollow noopener noreferrer" href={translate("footer.rrss.youtube")} target="_blank" className={styles['xxss']}>
                <span className="icon icon-youtube icon-expand" />
            </a>
            <a rel="nofollow noopener noreferrer" href={translate("footer.rrss.twitter")} target="_blank" className={styles['xxss']}>
                <span className="icon icon-twitter" />
            </a>
        </div>
    );
};

const FooterCopyright = () => {
    const year = new Date().getFullYear();
    const { translate } = useContext(I18nContext);
    return (
        <div className={styles['footer-mobile-copyright']}>
            <p>
                <a rel="nofollow" href={translate("footer.links.who_we_are_link")}>
                    <I18n>footer.links.who_we_are</I18n>
                </a>
                &nbsp;|&nbsp;
                <a rel="nofollow" href={translate("footer.links.help_link")}>
                    <I18n>footer.links.help</I18n>
                </a>
                &nbsp;|&nbsp;
                <a rel="nofollow" href={translate("footer.links.returns_link")}>
                    <I18n>footer.links.returns</I18n>
                </a>
                &nbsp;|&nbsp;
                <a rel="nofollow" href={translate("footer.links.shipping_link")}>
                    <I18n>footer.links.shipping</I18n>
                </a>
            </p>

            <p>
                <I18n>footer.legal.description</I18n>
            </p>

            <p>
                <span dangerouslySetInnerHTML={{
                    __html: translate("footer.legal.copyright", {
                        current_year: year
                    })
                }} />
                &nbsp;|&nbsp;
                <a rel="nofollow" href={translate("footer.links.legal_conditions_link")}>
                    <I18n>footer.links.legal_conditions</I18n>
                </a>
                &nbsp;|&nbsp;
                <a rel="nofollow" href={translate("footer.links.privacy_policy_link")}>
                    <I18n>footer.links.privacy_policy</I18n>
                </a>
                &nbsp;|&nbsp;
                <a rel="nofollow" href={translate("footer.links.cookies_policy_link")}>
                    <I18n>footer.links.cookies_policy</I18n>
                </a>
                &nbsp;|&nbsp;
                <a rel="nofollow" href={translate("footer.links.general_conditions_link")}>
                    <I18n>footer.links.general_conditions</I18n>
                </a>
            </p>

            <p>
                <I18n>footer.legal.cif</I18n>
            </p>
        </div>
    );
};

const FooterMobile = ({ model }) => {

    const { showBestPrice, showFinance, icons, rating } = model;

    return (
        <footer className={styles['footer-mobile-component']}>
            <FooterContent showBestPrice={showBestPrice} showFinance={showFinance} rating={rating} />
            <FooterSprite icons={icons} />
            <FooterXXSS />
            <FooterCopyright />
        </footer>
    );
};

FooterMobile.propTypes = {
    model: PropTypes.object
};

export default FooterMobile;
